import { ChangeDetectionStrategy, Component, computed, effect, inject, OnDestroy, Signal, signal, WritableSignal } from '@angular/core';
import { IonicModule } from '@ionic/angular';
import { OpenSpinner } from '@open/ngx-components/spinner';
import {
  Event as RouterEvent,
  NavigationCancel,
  NavigationCancellationCode,
  NavigationEnd,
  NavigationError,
  Router,
} from '@angular/router';
import { filter, Subscription, take } from 'rxjs';
import { BootLoaderService } from './services/boot-loader.service';

/** Kept in sync with the `transition` duration in `app.component.css`. */
const BOOT_OVERLAY_FADE_MS = 300;

const BOOT_OVERLAY_MAX_MS = 10_000;

@Component({
  selector: 'app-root',
  templateUrl: 'app.component.html',
  styleUrl: 'app.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [IonicModule, OpenSpinner],
})
export class AppComponent implements OnDestroy {
  #router: Router = inject(Router);
  #bootLoader: BootLoaderService = inject(BootLoaderService);

  hasNavigationSettled: WritableSignal<boolean> = signal(false);

  /** Also waits for the first page's content to report ready (`BootLoaderService`): navigation settling alone would fade the overlay right before the page's own spinner takes over, showing two loaders for one wait. */
  isOverlayVisible: Signal<boolean> = computed(() =>
    !this.hasNavigationSettled() || this.#bootLoader.isContentPending());

  /** Kept in the DOM, transparent and inert, for the duration of the fade-out, then dropped, so the overlay never intercepts a tap on the app it is fading away from. */
  isOverlayMounted: WritableSignal<boolean> = signal(true);

  #subscription: Subscription;
  #safetyTimer: ReturnType<typeof setTimeout>;
  #fadeTimer?: ReturnType<typeof setTimeout>;

  constructor() {
    this.#subscription = this.#router.events.pipe(
      filter((event: RouterEvent) => isSettledNavigation(event)),
      take(1),
    ).subscribe(() => this.hasNavigationSettled.set(true));

    this.#safetyTimer = setTimeout(() => {
      this.hasNavigationSettled.set(true);
      this.#bootLoader.releaseAll();
    }, BOOT_OVERLAY_MAX_MS);

    effect(() => {
      if (this.isOverlayVisible()) {
        clearTimeout(this.#fadeTimer);
        this.isOverlayMounted.set(true);
        return;
      }

      clearTimeout(this.#safetyTimer);
      this.#fadeTimer = setTimeout(() => this.isOverlayMounted.set(false), BOOT_OVERLAY_FADE_MS);
    });
  }

  ngOnDestroy(): void {
    this.#subscription.unsubscribe();
    clearTimeout(this.#safetyTimer);
    clearTimeout(this.#fadeTimer);
  }
}

/** A `NavigationCancel` with code `Redirect` is followed immediately by a second navigation — releasing the overlay there would uncover a blank app for the length of the redirect, so only that code fails to settle. */
function isSettledNavigation(event: RouterEvent): boolean {
  if (event instanceof NavigationEnd || event instanceof NavigationError) {
    return true;
  }

  return event instanceof NavigationCancel && event.code !== NavigationCancellationCode.Redirect;
}
